import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Paper, Typography, Tabs, Tab, Box, Select, MenuItem, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme, useMediaQuery } from '@mui/material';
import { ArrowBack } from "@mui/icons-material";
import Grid from '@mui/material/Grid2';
import axios from 'axios';
import DataLoader from '../components/common/DataLoader.js';
import config from '../config.js';
import { formatEuro, formatPct } from '../functions/formatNumber.js';
import { listAnysInversio, listMesos } from '../functions/common-lists.js';
import { strDateToDDMMYYYY } from '../functions/formatDate.js';
import HistoricInversioChart from '../components/dashboard-inversio/HistoricInversioChart.js';
import { ActiuCardMensual, TransaccioCardMensual } from '../components/dashboard-inversio/cards/index.js';

const DashboardInversioMensual = () => {

  const { userapp, anyo, mes } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tabValue, setTabValue] = useState(0);
  const [selectedAssetId, setSelectedAssetId] = useState(null);

  const parsedYear = parseInt(anyo, 10);
  const parsedMonth = parseInt(mes, 10);

  const fetchFunction = (jwtToken, onData, onError, onFinally) => {
    axios.get(`${config.apiBaseUrl}/v1/resummensualinversio/${userapp}/${anyo}/${mes}`, {
      headers: { Authorization: `${jwtToken}` }
    })
    .then((response) => {
      onData(response);
    })
    .catch((err) => {
      onError(err, 'carregar dades de inversió mensual');
    })
    .finally(() => onFinally());
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleYearMonthChange = (year, month) => {
    setSelectedAssetId(null);
    navigate(`/${userapp}/inversio/${year}/mes/${month}`);
  };

  const gotoBack = () => {
    navigate(`/${userapp}/inversio/${anyo}`);
  };

  const handleRowClick = (assetId) => {
    setSelectedAssetId(prev => prev === assetId ? null : assetId);
  };

  const formatAportatRetirat = (aportat, retirat) => {
    const apStr = aportat > 0 ? `+${formatEuro(aportat)}` : '0,00 €';
    const retiradoNeto = retirat; // En el backend, los retiros ya se calculan positivos en el sumatorio
    const retStr = retiradoNeto > 0 ? `-${formatEuro(retiradoNeto)}` : '0,00 €';
    return `${apStr} / ${retStr}`;
  };

  const getAssetName = (actiuId, assets) => {
    const asset = assets.find((a) => a.actiuId === actiuId);
    return asset ? asset.nom : `Actiu #${actiuId}`;
  };

  const getTxTypeColor = (type) => {
    if ('COMPRA'.equalsIgnoreCase ? 'COMPRA'.equalsIgnoreCase(type) : type === 'COMPRA') return '#4CAF50';
    if ('VENDA'.equalsIgnoreCase ? 'VENDA'.equalsIgnoreCase(type) : type === 'VENDA') return '#EF4444';
    return '#FF9800'; // Ajust / Otros
  };

  return (
    <DataLoader id={`${anyo}-${mes}`} fetchFunction={fetchFunction} goBackLocation={`/${userapp}/inversio/${anyo}`}>
      {(data) => {
        const assets = data.actius || [];
        const transaccions = data.transaccions || [];

        const rendimientoTotal = data.rendimientoTotal || 0;
        const colorGP = rendimientoTotal >= 0 ? '#4CAF50' : '#EF4444';
        const colorGPBg = rendimientoTotal >= 0 ? 'rgba(76, 175, 80, 0.12)' : 'rgba(239, 68, 68, 0.12)';

        const baseCapital = (data.saldoInicial || 0) + (data.aportacions || 0);
        const percentatgeRetorn = data.percentatgeRetorn !== undefined && data.percentatgeRetorn !== null
          ? data.percentatgeRetorn
          : (baseCapital > 0 ? (rendimientoTotal / baseCapital) * 100 : 0);

        return (
          <>
            <Grid size={12} sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
              <IconButton onClick={gotoBack}>
                <ArrowBack />
              </IconButton>
              <Typography variant="h5">
                Dashboard Inversió Mensual
              </Typography>
              
              <Select
                value={parsedYear}
                onChange={(e) => handleYearMonthChange(e.target.value, parsedMonth)}
                size="small"
                sx={{ ml: 2 }}
              >
                {listAnysInversio().map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>

              <Select
                value={parsedMonth}
                onChange={(e) => handleYearMonthChange(parsedYear, e.target.value)}
                size="small"
                sx={{ ml: 1 }}
              >
                {listMesos.map((m) => (
                  <MenuItem key={m.key} value={m.key}>
                    {m.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            {/* Tarjetas resumen superior y contenido envuelto en un único Grid container sin width: 100% */}
            <Grid container spacing={3} sx={{ marginTop: '10px' }}>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Paper sx={{ padding: '12px', textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="text.secondary">Saldo Inicial (1 de Mes)</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#666666', mt: 0.5 }}>
                    {formatEuro(data.saldoInicial || 0)}
                  </Typography>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, sm: 3 }}>
                <Paper sx={{ padding: '12px', textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="text.secondary">Saldo Final (Fi de Mes)</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#3F51B5', mt: 0.5 }}>
                    {formatEuro(data.saldoFinal || 0)}
                  </Typography>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, sm: 3 }}>
                <Paper sx={{ padding: '12px', textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="text.secondary">Aportat / Retirat</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 0.5, color: '#666666' }}>
                    {formatAportatRetirat(data.aportacions || 0, data.retiros || 0)}
                  </Typography>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, sm: 3 }}>
                <Paper sx={{ padding: '12px', textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="text.secondary">Rendiment Mensual</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', mt: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: colorGP }}>
                      {rendimientoTotal >= 0 ? '+' : ''}{formatEuro(rendimientoTotal)}
                    </Typography>
                    <Box sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      backgroundColor: colorGPBg,
                      color: colorGP,
                      border: `1px solid ${rendimientoTotal >= 0 ? '#A5D6A7' : '#FFCDD2'}`,
                      borderRadius: '12px',
                      px: 1,
                      py: 0.2,
                      fontSize: '0.85rem',
                      fontWeight: 'bold',
                    }}>
                      {percentatgeRetorn >= 0 ? '+' : ''}{formatPct(percentatgeRetorn)}
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* Gráfico de evolución */}
              <Grid size={12} sx={{ mt: 1 }}>
                <HistoricInversioChart 
                  assets={assets} 
                  isDaily={true} 
                  selectedAssetId={selectedAssetId} 
                />
              </Grid>

              {/* Pestañas de detalle */}
              <Grid size={12} sx={{ mt: 1 }}>
                <Paper>
                <Tabs value={tabValue} onChange={handleTabChange} indicatorColor="primary" textColor="primary" centered>
                  <Tab label="Actius" />
                  <Tab label={`Transaccions (${transaccions.length})`} />
                </Tabs>

                {/* Pestanya 1: Actius */}
                {tabValue === 0 && (
                  <Box sx={{ p: isMobile ? 1 : 2 }}>
                    {isMobile ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {assets.map((row) => (
                          <ActiuCardMensual
                            key={row.actiuId}
                            asset={row}
                            isSelected={selectedAssetId === row.actiuId}
                            onClick={() => handleRowClick(row.actiuId)}
                          />
                        ))}
                        {assets.length === 0 && (
                          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                            No hi ha actius registrats
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>Nom actiu</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Saldo Final</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Aportat / Retirat</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Guany / Pèrdua</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Rendiment</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Rentabilitat (%)</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {assets.map((row) => {
                              const gp = (row.saldoFinal || 0) - (row.saldoInicial || 0);
                              const gpColor = gp >= 0 ? '#4CAF50' : '#EF4444';
                              const rendColor = (row.rendimientoTotal || 0) >= 0 ? '#4CAF50' : '#EF4444';
                              const retVal = row.percentatgeRetorn !== undefined && row.percentatgeRetorn !== null
                                ? row.percentatgeRetorn
                                : null;
                              const retColor = (retVal || 0) >= 0 ? '#4CAF50' : '#EF4444';
                              const isSelected = selectedAssetId === row.actiuId;
                              return (
                                <TableRow
                                  key={row.actiuId}
                                  hover
                                  onClick={() => handleRowClick(row.actiuId)}
                                  sx={{
                                    cursor: 'pointer',
                                    backgroundColor: isSelected ? 'rgba(63, 81, 181, 0.08)' : 'inherit',
                                    '&:hover': {
                                      backgroundColor: isSelected ? 'rgba(63, 81, 181, 0.12) !important' : 'rgba(0, 0, 0, 0.04)',
                                    }
                                  }}
                                >
                                  <TableCell sx={{ fontWeight: isSelected ? 'bold' : 'normal' }}>
                                    {row.nom}
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatEuro(row.saldoFinal)}</TableCell>
                                  <TableCell align="center">
                                    {formatAportatRetirat(row.aportacions, row.retiros)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ color: gpColor, fontWeight: 'bold' }}>
                                    {gp >= 0 ? '+' : ''}{formatEuro(gp)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ color: rendColor, fontWeight: 'bold' }}>
                                    {(row.rendimientoTotal || 0) >= 0 ? '+' : ''}{formatEuro(row.rendimientoTotal)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ color: retColor, fontWeight: 'bold' }}>
                                    {retVal !== null ? `${retVal >= 0 ? '+' : ''}${formatPct(retVal)}` : '-'}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                )}

                {/* Pestanya 2: Transaccions */}
                {tabValue === 1 && (
                  <Box sx={{ p: isMobile ? 1 : 2 }}>
                    {isMobile ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {transaccions.map((row) => (
                          <TransaccioCardMensual
                            key={row.id}
                            tx={row}
                            assetName={getAssetName(row.actiuId, assets)}
                          />
                        ))}
                        {transaccions.length === 0 && (
                          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                            No hi ha transaccions d'inversió registrades en aquest mes
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>Data</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Actiu</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Tipus</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Quantitat</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Preu Unitari</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {transaccions.map((row) => {
                              const txColor = getTxTypeColor(row.tipusTransaccio);
                              return (
                                <TableRow key={row.id} hover>
                                  <TableCell>{strDateToDDMMYYYY(row.fecha)}</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>
                                    {getAssetName(row.actiuId, assets)}
                                  </TableCell>
                                  <TableCell align="center">
                                    <Box sx={{ 
                                      backgroundColor: `${txColor}1A`, 
                                      color: txColor, 
                                      borderRadius: '4px', 
                                      display: 'inline-block',
                                      px: 1.5, 
                                      py: 0.2, 
                                      fontSize: '0.8rem',
                                      fontWeight: 'bold'
                                    }}>
                                      {row.tipusTransaccio}
                                    </Box>
                                  </TableCell>
                                  <TableCell align="right">{row.quantitat}</TableCell>
                                  <TableCell align="right">{formatEuro(row.preuUnitariEur)}</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatEuro(row.totalEur)}</TableCell>
                                </TableRow>
                              );
                            })}
                            {transaccions.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={6} align="center">
                                  No hi ha transaccions d'inversió registrades en aquest mes
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      );
    }}
    </DataLoader>
  );
};

export default DashboardInversioMensual;
